import React, { useState, useEffect, useMemo, use } from 'react'
import { useDispatch } from 'react-redux' 
import { useTranslation } from 'react-i18next'
import { useParams, useHistory } from 'react-router-dom'
import { createColumnHelper, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table'

import styles from './styles.module.scss'

import { getEmployeesDocuments, getCompanyEmployeesAll, getPlaces } from '../../../api'
import { showDocumentForm } from '../../../store/documents/actions'
import { generateResourcesFromEmployees, getCheckedEmployeeIds } from '../../TimeOff/Calendar/utils'
import usePermissions from '../../Core/usePermissions'

import { PlusIcon } from '../Icons' 
import Table from '../../Core/Table'
import SearchIcon from '../../Icons/SearchIcon'
import Input from '../../Core/Input/Input'
import CustomSelect from '../../Core/CustomSelect'

const columnHelper = createColumnHelper()

const getInitials = (name, surname) => {
  return `${(name || '').charAt(0)}${(surname || '').charAt(0)}`.toUpperCase()
}

const getPlacesLabel = (places) => {
  if (Array.isArray(places)) {
    return places.length ? places.join(', ') : null
  }
  return places || null
}

const ChevronIcon = () => (
  <svg width="7" height="12" viewBox="0 0 7 12" fill="none">
    <path d="M1 1L6 6L1 11" stroke="#808F94" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const EmployeesDocuments = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const { id: companyId } = useParams()
  const permissions = usePermissions([{
    name: 'can_upload_documents',
    permission: 'documents_can_upload_documents'
  }])

  const [loading, setLoading] = useState(false)
  const [employeesWithDocuments, setEmployeesWithDocuments] = useState([])
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState({
    employees: [],
    places: [],
  })

  const checkedEmployeeIds = getCheckedEmployeeIds(filters.employees)
  const checkedPlaceIds = getCheckedEmployeeIds(filters.places)

  useEffect(() => {
    if (companyId) {
      init(companyId)
      getEmployeesDocumentsData(companyId)
    }
  }, [companyId])

  const columns = useMemo(() => [
    columnHelper.display({
      id: 'employee',
      header: t('Employee'),
      cell: ({ row }) => (
        <div className={styles.employeeCell}>
          {
            row.original.employee_avatar
              ? <div className={styles.avatar}>
                  <img src={`${window.location.origin}/storage/${row.original.employee_avatar}`} alt={row.original.employee_full_name} /> 
                </div>
              : <div className={styles.initials}>{getInitials(row.original.employee_name, row.original.employee_surname)}</div>
          }
          <div>
            <div className={styles.employeeName}>{ row.original.employee_full_name }</div>
            <div className={styles.employeeRole}>{ row.original.company_role_name }</div>
          </div>
        </div>
      ),
    }),
    columnHelper.display({
      id: 'place',
      header: t('Place'),
      cell: ({ row }) => {
        const label = getPlacesLabel(row.original.places)
        return label ? <span>{ label }</span> : <span className={styles.mutedText}>-</span>
      },
    }),
    columnHelper.accessor('documents_count', {
      header: t('Documents'),
      cell: ({ getValue }) => <span>{ getValue() || 0 }</span>,
    }),
    columnHelper.accessor('pending_actions_count', {
      header: t('Pending actions'),
      cell: ({ getValue }) => {
        const value = getValue()
        return value ? <span>{ value }</span> : <span className={styles.mutedText}>-</span>
      },
    }),
    columnHelper.accessor('needs_attention_count', {
      header: t('Needs attention'),
      enableSorting: false,
      cell: ({ getValue }) => {
        const value = getValue()
        if (!value) {
          return <span className={styles.mutedText}>-</span>
        }
        return (
          <div className={styles.attentionBadge}>
            <div className={styles.attentionDot} />
            { value === 1 ? t('1 item') : t('{{count}} items', { count: value }) }
          </div>
        )
      },
    }),
    columnHelper.display({
      id: 'chevron',
      enableSorting: false,
      header: '',
      cell: () => (
        <div className={styles.chevronCell}>
          <ChevronIcon />
        </div>
      ),
    }),
  ], [t])

  const init = async (companyId) => {
    const [employeesRes, placesRes] = await Promise.all([
      getCompanyEmployeesAll(companyId),
      getPlaces(companyId),
    ])
    setFilters({
      employees: employeesRes?.success ? generateResourcesFromEmployees(employeesRes?.users) : [],
      places: Array.isArray(placesRes) ? placesRes.map(p => ({id: p.id, title: p.name, checked: false, isEmployee: true})) : []
    })
  }

  const getEmployeesDocumentsData = async (companyId) => {
    setLoading(true)
    try {
      const res = await getEmployeesDocuments(companyId)
      if (Array.isArray(res)) {
        setEmployeesWithDocuments(res)
      }
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateDocument = () => {
    dispatch(showDocumentForm())
  }

  const handleOpenEmployee = (employee) => {
    history.push({search: `?tab=employees&employee_id=${employee.employee_id}`})
  }

  const filteredEmployees = useMemo(() => {
    return employeesWithDocuments.filter(employee => {
      return (
        (!search || (employee.employee_full_name || '').toLowerCase().includes(search.toLowerCase())) &&
        (checkedEmployeeIds.length === 0 || checkedEmployeeIds.includes(employee.employee_id)) &&
        (checkedPlaceIds.length === 0 || checkedPlaceIds.some(id => employee.place_ids.includes(id)))
      )
    })
  }, [search, employeesWithDocuments, checkedEmployeeIds, checkedPlaceIds])

  const table = useReactTable({
    data: filteredEmployees,
    columns,
    getRowId: (row) => {
      return `${row.document_id}_${row.employee_id}`
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerInfo}>
          <div className={styles.headerTitle}>{t('Employees')}</div>
          <div className={styles.headerDesc}>{t('Document coverage per employee')}</div>
        </div>
        {
          permissions.can_upload_documents
            ? <button type="button" className={styles.button} onClick={handleCreateDocument}>
                <PlusIcon />
                { t('Upload document') }
              </button>
            : null
        }
      </div>
      <div className={styles.content}>
        <div className={styles.searchWrapper}>
          <Input
            icon={<SearchIcon />}
            placeholder={`${t('Search')}...`}
            width='100%'
            height='36px'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className={styles.filterItem}>
            <CustomSelect
              placeholder={t('All employees')}
              items={filters.employees}
              onChange={data => {
                setFilters(prev => ({...prev, employees: data}))
              }}/>
          </div>
          <div className={styles.filterItem}>
            <CustomSelect
              placeholder={t('All places')}
              items={filters.places}
              className={styles.filterItemSelect}
              onChange={data => {
                setFilters(prev => ({...prev, places: data}))
              }} />
          </div>
        </div>
        <Table
          table={table}
          columns={columns}
          loading={loading}
          headerStyles={{padding: '6px 10px'}}
          placeholder={t('No documents found')}
          onClickRow={handleOpenEmployee}
        />
      </div>
    </div>
  )
}

export default EmployeesDocuments
